import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { BarChart } from '../financial/_finHelpers';

export default function StatisticsCalc() {
  const [input, setInput] = useState('4, 8, 15, 16, 23, 42, 50, 8');
  const c = useMemo(() => {
    const arr = input.split(/[,\s]+/).map(x => parseFloat(x)).filter(x => !isNaN(x)).sort((a,b)=>a-b);
    if (!arr.length) return null;
    const n = arr.length;
    const sum = arr.reduce((a,v)=>a+v,0);
    const mean = sum/n;
    const median = n % 2 ? arr[Math.floor(n/2)] : (arr[n/2-1] + arr[n/2])/2;
    const freq = {}; arr.forEach(v => { freq[v] = (freq[v]||0)+1; });
    const maxF = Math.max(...Object.values(freq));
    const mode = Object.keys(freq).filter(k => freq[k] === maxF).join(', ');
    const range = arr[n-1] - arr[0];
    const q1 = arr[Math.floor(n/4)]; const q3 = arr[Math.floor(3*n/4)];
    const varP = arr.reduce((s,v)=>s+(v-mean)**2, 0)/n;
    const varS = arr.reduce((s,v)=>s+(v-mean)**2, 0)/(n-1||1);
    return { arr, freq, n, sum, mean, median, mode, range, min: arr[0], max: arr[n-1], q1, q3, iqr: q3-q1, sdP: Math.sqrt(varP), sdS: Math.sqrt(varS) };
  }, [input]);
  return (
    <CalcShell look="cnet" title="Statistics Calculator" subtitle="Compute mean, median, mode, quartiles, range, standard deviation — plus a frequency bar graph.">
      <div className="field"><label>Data Set (comma or space separated)</label>
        <textarea rows={3} value={input} onChange={e => setInput(e.target.value)} data-testid="stat-input"/>
      </div>
      {c && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <table className="data-table"><tbody>
              <tr><td>Count</td><td className="text-right">{c.n}</td></tr>
              <tr><td>Sum</td><td className="text-right">{c.sum}</td></tr>
              <tr><td>Mean (arithmetic average)</td><td className="text-right font-semibold text-blue-700">{c.mean.toFixed(4)}</td></tr>
              <tr><td>Median</td><td className="text-right font-semibold text-purple-700">{c.median}</td></tr>
              <tr><td>Mode</td><td className="text-right font-semibold text-green-700">{c.mode}</td></tr>
              <tr><td>Range</td><td className="text-right font-semibold text-orange-600">{c.range}</td></tr>
            </tbody></table>
            <table className="data-table"><tbody>
              <tr><td>Min / Max</td><td className="text-right">{c.min} / {c.max}</td></tr>
              <tr><td>Q1 / Q3</td><td className="text-right">{c.q1} / {c.q3}</td></tr>
              <tr><td>IQR</td><td className="text-right">{c.iqr}</td></tr>
              <tr><td>Variance (population)</td><td className="text-right">{(c.sdP*c.sdP).toFixed(4)}</td></tr>
              <tr><td>SD (population)</td><td className="text-right font-semibold">{c.sdP.toFixed(4)}</td></tr>
              <tr><td>SD (sample)</td><td className="text-right">{c.sdS.toFixed(4)}</td></tr>
            </tbody></table>
          </div>

          <div className="calc-card p-5 mt-6">
            <h3 className="font-bold text-slate-900 mb-3">Value Frequency Bar Graph</h3>
            <BarChart
              data={Object.keys(c.freq).map(k => ({
                label: k,
                values: [{ value: c.freq[k], color: (+k === c.mean.toFixed(0)*1) ? '#2563eb' : (+k === c.median) ? '#7c3aed' : c.mode.split(',').map(x => x.trim()).includes(k) ? '#10b981' : '#94a3b8', label: `Freq of ${k}` }],
              }))}
              stack={true}
              yLabel=""
              legend={[
                { label: 'Mean bucket',  color: '#2563eb' },
                { label: 'Median value', color: '#7c3aed' },
                { label: 'Mode value(s)', color: '#10b981' },
                { label: 'Other',        color: '#94a3b8' },
              ]}
            />
          </div>
          <StepsPanel steps={[
            `Mean = Σx / n = ${c.sum} / ${c.n} = <b>${c.mean.toFixed(4)}</b>`,
            `Sorted values → median = <b>${c.median}</b>`,
            `Mode(s) = <b>${c.mode}</b>. Range = max − min = ${c.max} − ${c.min} = <b>${c.range}</b>.`,
          ]}/>
        </>
      )}
    </CalcShell>
  );
}
